import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Brain, AlertTriangle, TrendingDown, Package } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const ForecastPage = () => {
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchForecast = async () => {
      try {
        const res = await authFetch(`${API}/factory/forecast/raw-materials`);
        const data = await res.json();
        if (data.success) setForecasts(data.data || []);
      } catch { toast.error("Failed to load forecast"); }
      finally { setLoading(false); }
    };
    fetchForecast();
  }, []);

  const getStatusColor = (daysLeft) => {
    if (daysLeft <= 3) return "text-red-400 bg-red-900/30";
    if (daysLeft <= 7) return "text-amber-400 bg-amber-900/30";
    return "text-emerald-400 bg-emerald-900/30";
  };

  const getProgressColor = (daysLeft) => {
    if (daysLeft <= 3) return "bg-red-500";
    if (daysLeft <= 7) return "bg-amber-500";
    return "bg-emerald-500";
  };

  return (
    <div className="space-y-6" data-testid="forecast-page">
      <div>
        <h1 className="text-2xl font-bold text-white font-['Sora']">AI Raw Material Forecast</h1>
        <p className="text-emerald-600 text-sm mt-1">Predicted days-left and reorder suggestions based on consumption patterns</p>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div>
      ) : forecasts.length === 0 ? (
        <Card className="bg-[#1C2B2B] border-emerald-900/30 p-8 text-center">
          <Brain className="w-10 h-10 text-emerald-700 mx-auto mb-3" />
          <p className="text-slate-400">No data available for forecasting. Add material entries to get predictions.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {forecasts.map((f, i) => (
            <Card key={i} className="bg-[#1C2B2B] border-emerald-900/30 p-5 hover:border-emerald-700/50 transition-colors" data-testid={`forecast-card-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-white font-semibold">{f.material}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Stock: <span className="text-emerald-400 font-mono">{f.current_stock} MT</span>
                    {f.daily_consumption > 0 && <> &middot; Daily use: <span className="text-slate-300 font-mono">{f.daily_consumption} MT</span></>}
                  </p>
                </div>
                <Badge className={`text-xs ${getStatusColor(f.days_left)}`}>
                  {f.days_left <= 0 ? "OUT OF STOCK" : `${f.days_left} days left`}
                </Badge>
              </div>
              
              <div className="mb-3">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Stock Level</span>
                  <span>{Math.min(100, Math.round((f.current_stock / (f.min_level || 1)) * 100))}%</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${getProgressColor(f.days_left)}`} style={{ width: `${Math.min(100, (f.current_stock / Math.max(f.min_level * 2, 1)) * 100)}%` }} />
                </div>
              </div>

              {f.days_left <= 7 && (
                <div className="flex items-center gap-2 p-2 rounded-md bg-red-900/20 border border-red-900/30">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                  <p className="text-xs text-red-300">
                    {f.days_left <= 0 ? "Immediate restock required!" : f.days_left <= 3 ? "Critical: Reorder immediately" : "Low stock warning: Plan reorder"}
                  </p>
                </div>
              )}

              {f.suggested_order_mt > 0 && (
                <div className="mt-2 text-xs text-slate-400">
                  Suggested order: <span className="text-emerald-400 font-mono font-medium">{f.suggested_order_mt} MT</span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ForecastPage;
